import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { useAuth } from "@/context/AuthContext";
import { DatabaseIcon, SearchIcon, DownloadIcon, EditIcon } from "lucide-react";

export default function BancoDados() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/audit/database");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!query) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) => Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(q)));
  }, [rows, query]);

  const exportXLSX = () => {
    const ws = XLSX.utils.json_to_sheet(filtered);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BD_Global");
    XLSX.writeFile(wb, `bd-global-${Date.now()}.xlsx`);
    toast.success("Excel exportado");
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: "application/json" });
    download(blob, `bd-global-${Date.now()}.json`);
    toast.success("JSON exportado");
  };

  const exportXML = () => {
    const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<wms_database>\n` +
      filtered.map((r) =>
        `  <registro>\n` +
        Object.entries(r).map(([k, v]) => `    <${k}>${escapeXml(String(v ?? ""))}</${k}>`).join("\n") +
        `\n  </registro>`
      ).join("\n") +
      `\n</wms_database>`;
    const blob = new Blob([xml], { type: "application/xml" });
    download(blob, `bd-global-${Date.now()}.xml`);
    toast.success("XML exportado");
  };

  const exportPython = () => {
    const py = `# WMS Banco de Dados Global — export ${new Date().toISOString()}\n` +
      `import pandas as pd\n\n` +
      `data = ${JSON.stringify(filtered, null, 2)}\n\n` +
      `df = pd.DataFrame(data)\n` +
      `print(df.head())\n`;
    const blob = new Blob([py], { type: "text/x-python" });
    download(blob, `bd-global-${Date.now()}.py`);
    toast.success("Script Python exportado");
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      no_serie: row.no_serie || "",
      no_lote: row.no_lote || "",
      nf_produto: row.nf_produto || "",
      nf_recebimento: row.nf_recebimento || "",
      cliente: row.cliente || "",
      no_pedido: row.no_pedido || "",
      motorista: row.motorista || "",
      placa: row.placa || "",
    });
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/cycles/${editing.id}`, form);
      toast.success(`Salvo · propagado: ${(data.propagated || []).join(", ") || "nenhum"}`);
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1900px] mx-auto">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mb-1">Auditoria · Repositório mestre</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-none">Banco de Dados Global</h1>
          <p className="text-sm text-muted-foreground mt-2">Todas as movimentações da frota — filtro instantâneo + exportação multi-formato.</p>
        </div>
        <DatabaseIcon className="w-12 h-12 text-[#00E5FF]" strokeWidth={1.2} />
      </header>

      <Card className="bg-[#14151A] border-border rounded-none p-4 mb-3">
        <div className="flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Filtro dinâmico</Label>
            <div className="relative mt-1">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="bd-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="N° Série, NF, Pedido, Cliente, Lote..."
                autoFocus
                className="pl-9 rounded-none bg-[#0A0A0C] font-mono h-10"
              />
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="bd-export" className="rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 h-10 px-6 font-bold uppercase tracking-wider text-xs">
                <DownloadIcon className="w-4 h-4 mr-2" /> Exportar Base
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#14151A] border-border rounded-none">
              <DropdownMenuItem onClick={exportXLSX} data-testid="export-xlsx">📊 Excel (.xlsx)</DropdownMenuItem>
              <DropdownMenuItem onClick={exportXML} data-testid="export-xml">📄 XML (NFe)</DropdownMenuItem>
              <DropdownMenuItem onClick={exportJSON} data-testid="export-json">{`{} JSON (API)`}</DropdownMenuItem>
              <DropdownMenuItem onClick={exportPython} data-testid="export-python">🐍 Python (pandas)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{filtered.length} de {rows.length} registros</div>
        </div>
      </Card>

      <Card className="bg-[#14151A] border-border rounded-none p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#0A0A0C] text-[10px] uppercase tracking-widest text-muted-foreground sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left">ID</th>
                <th className="px-2 py-2 text-left">Status</th>
                <th className="px-2 py-2 text-left">N° Série</th>
                <th className="px-2 py-2 text-left">Produto</th>
                <th className="px-2 py-2 text-left">Saída</th>
                <th className="px-2 py-2 text-left">Retorno</th>
                <th className="px-2 py-2 text-left">Cliente</th>
                <th className="px-2 py-2 text-right">Qtd</th>
                <th className="px-2 py-2 text-left">Pedido</th>
                <th className="px-2 py-2 text-left">NF Produto</th>
                <th className="px-2 py-2 text-left">NF Recb.</th>
                <th className="px-2 py-2 text-left">N° Lote</th>
                <th className="px-2 py-2 text-left">Validade</th>
                <th className="px-2 py-2 text-left">Resp.</th>
                {isAdmin && <th className="px-2 py-2 text-right">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="15" className="text-center text-muted-foreground py-8">Nenhum registro.</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-border/60 hover:bg-[#1C1D24]" data-testid={`bd-row-${r.id}`}>
                  <td className="px-2 py-2 font-mono text-muted-foreground">#{r.registro_id}</td>
                  <td className="px-2 py-2">
                    {r.em_poder_cliente ? (
                      <Badge className="rounded-none border bg-[#FFD60A]/15 text-[#FFD60A] border-[#FFD60A]/40 font-bold">{r.status_view}</Badge>
                    ) : (
                      <Badge className="rounded-none border bg-transparent border-border text-[10px]">{r.status_view}</Badge>
                    )}
                  </td>
                  <td className="px-2 py-2 font-mono text-[#FFD60A]">{r.no_serie}</td>
                  <td className="px-2 py-2">{r.codigo_produto && <span className="text-[#FFD60A] font-mono mr-1">#{r.codigo_produto}</span>}{r.nome_produto}</td>
                  <td className="px-2 py-2 font-mono text-[10px]">{r.data_saida && new Date(r.data_saida).toLocaleString("pt-BR")}</td>
                  <td className="px-2 py-2 font-mono text-[10px]">{r.data_retorno && new Date(r.data_retorno).toLocaleString("pt-BR")}</td>
                  <td className={`px-2 py-2 ${r.em_poder_cliente ? "bg-[#FFD60A]/10 text-[#FFD60A] font-bold" : ""}`}>{r.cliente || "—"}</td>
                  <td className="px-2 py-2 text-right font-mono">{r.qtd_envasado}</td>
                  <td className="px-2 py-2 font-mono">{r.no_pedido || "—"}</td>
                  <td className="px-2 py-2 font-mono">{r.nf_produto || "—"}</td>
                  <td className="px-2 py-2 font-mono">{r.nf_recebimento || "—"}</td>
                  <td className="px-2 py-2 font-mono">{r.no_lote}</td>
                  <td className="px-2 py-2 font-mono">{r.data_validade_lote}</td>
                  <td className="px-2 py-2 text-[10px]">
                    {r.usuario_envase && <div>E: {r.usuario_envase}</div>}
                    {r.usuario_expedicao && <div>X: {r.usuario_expedicao}</div>}
                    {r.usuario_recebimento_vazio && <div>R: {r.usuario_recebimento_vazio}</div>}
                  </td>
                  {isAdmin && (
                    <td className="px-2 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`bd-edit-${r.id}`} className="h-7 w-7 p-0">
                        <EditIcon className="w-3 h-3" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Admin edit modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-2xl" data-testid="bd-edit-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Correção de Logística (Administrador)</DialogTitle>
            <div className="text-xs text-muted-foreground mt-1">
              Registro #{editing?.registro_id} · alterações em N° Série propagam em cascata para containers + cycles.
            </div>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {Object.keys(form).map((k) => (
              <div key={k}>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{k.replace(/_/g, " ")}</Label>
                <Input
                  value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`bd-field-${k}`}
                  className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10"
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-none border-border">Cancelar</Button>
            <Button onClick={saveEdit} disabled={busy} data-testid="bd-save" className="rounded-none bg-[#FFD60A] text-black hover:bg-[#ffdf3a] font-bold uppercase tracking-wider">
              {busy ? "Propagando..." : "Salvar & Propagar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function escapeXml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
